import { motion } from 'framer-motion';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';

const faqs = [
  {
    q: 'What is included in the Vegas to Hollywood day trip?',
    a: 'Our all-inclusive tour covers luxury air-conditioned transportation, a professional guide, complimentary breakfast (pastry and juice), a proper sit-down lunch at an American restaurant near Warner Bros Studios, bottled water throughout the day, and multiple extended photo stops at Santa Monica Beach & Pier, Warner Bros Studios, Beverly Hills & Rodeo Drive, Sunset Boulevard, celebrity mansions, and the Hollywood Walk of Fame.',
  },
  {
    q: 'Is lunch really at a sit-down restaurant?',
    a: 'Absolutely! We take pride in offering a genuine restaurant dining experience. You sit inside at a real American-style restaurant — this is not a drive-through or fast food stop. Vegetarian and Halal-friendly options are available for all dietary needs. Lunch is fully included in your $215 tour price.',
  },
  {
    q: 'Which Las Vegas hotels do you pick up from?',
    a: 'We offer complimentary pickup from over 100 Las Vegas hotels including all major properties on the Strip (Bellagio, MGM Grand, Caesars Palace, ARIA, Venetian, Wynn, and many more), Downtown/Fremont Street locations, and select off-Strip hotels. If your hotel is not on our standard list, just call us and we will make arrangements.',
  },
  {
    q: 'How long does the tour last?',
    a: 'The Vegas to Hollywood tour is a full-day experience. We depart early morning from Las Vegas and travel to California, giving you a packed day exploring Hollywood, Beverly Hills, Santa Monica, and all the most iconic attractions. You return to your Las Vegas hotel in the evening, full of incredible memories.',
  },
  {
    q: 'Can I book last-minute or same-day tours?',
    a: 'Yes! Our reservation team operates 24 hours a day, 7 days a week. Same-day and last-minute bookings are welcome based on availability. Call us toll-free at 1-877-741-8390 (USA) or +1 (702) 941-5144 (international) and we will do our best to accommodate you.',
  },
  {
    q: 'Is this tour suitable for families with young children?',
    a: 'Our tour is completely family-friendly and loved by guests of all ages. Children are fascinated by the Hollywood Sign, Santa Monica Beach, and discovering celebrity stars on the Walk of Fame. The included sit-down restaurant lunch is especially convenient for families with young ones who need a proper meal break.',
  },
  {
    q: 'Do you offer group rates for large parties?',
    a: 'We specialize in coordinating tours for large groups including church groups, corporate teams, school trips, family reunions, and conventions. Groups of 15 or more qualify for special group pricing starting at $185 per person. Contact us directly for customized group packages and coordination.',
  },
  {
    q: 'What makes your tour different from other operators?',
    a: 'We are the premier Las Vegas to Hollywood tour service with over 10,000 five-star reviews. Unlike competitors, every tour includes a real sit-down restaurant lunch (not fast food), ample time to exit and explore at every stop rather than drive-by viewing, small intimate groups for personal attention, and luxury air-conditioned vehicles. We never rush — you get the full experience.',
  },
  {
    q: 'My spouse is attending a Las Vegas convention. Is this a good option?',
    a: 'This is the number one activity for convention companions in Las Vegas! We pick up directly from the Las Vegas Convention Center area and over 100 hotels. You enjoy a full day of Hollywood sightseeing while your partner attends sessions, and you are back by evening to share stories over dinner. No planning or driving stress — we handle everything.',
  },
  {
    q: 'What about private tours? What is different?',
    a: 'Private tours offer an exclusive, personalized Hollywood experience for your group. You travel in a luxury SUV with your own dedicated guide, enjoy a flexible customizable itinerary, and dine at The Cheesecake Factory for lunch. Private tours from Las Vegas are ideal for honeymoons, anniversary celebrations, birthday trips, VIP experiences, and intimate groups seeking the ultimate luxury Las Vegas tour. Accommodates up to 7 guests per vehicle. Call us to arrange your VIP experience.',
  },
  {
    q: 'What are the best things to do in Las Vegas besides casinos?',
    a: 'The Vegas to Hollywood tour is consistently rated the number one thing to do in Las Vegas beyond gambling and nightlife. Other popular Las Vegas activities include visiting the Grand Canyon, Hoover Dam, and Red Rock Canyon. However, our Hollywood day trip is the only Las Vegas attraction that includes breakfast, a sit-down restaurant lunch, and visits to Santa Monica Beach, Beverly Hills, the Walk of Fame, and the Hollywood Sign — all in one day for just $215 per person. It is the highest rated Las Vegas sightseeing tour with over 10,000 five-star reviews.',
  },
  {
    q: 'Do you pick up from Las Vegas Convention Center area hotels?',
    a: 'Yes! We offer complimentary pickup from hotels near the Las Vegas Convention Center, Mandalay Bay Convention Center, Sands Expo, and all major convention venues. Our Las Vegas convention companion tour is the top-rated activity for partners and spouses of convention attendees. While your companion attends sessions, you enjoy a full day exploring Hollywood, Beverly Hills, and Santa Monica Beach. We drop you back at your hotel by evening. This is the most popular convention companion activity in Las Vegas.',
  },
  {
    q: 'Can Canadian visitors easily book this Las Vegas tour?',
    a: 'Absolutely! Canadian travelers are among our most enthusiastic guests. Visitors from Toronto, Vancouver, Calgary, Edmonton, Montreal, Ottawa, Winnipeg, and all Canadian cities are warmly welcomed. We accept Canadian credit cards, and our toll-free number 1-877-741-8390 works from both the United States and Canada at no charge. Many Canadian tourists specifically plan their Las Vegas vacation around our Hollywood day trip. We also accommodate French-speaking guests from Quebec — just let us know when booking.',
  },
  {
    q: 'How does the Vegas to Hollywood tour compare to other Las Vegas day trips?',
    a: 'While Las Vegas offers several day trip options including Grand Canyon tours, Hoover Dam visits, and Death Valley excursions, our Vegas to Hollywood tour is the only day trip that takes you to California and includes both breakfast and a sit-down restaurant lunch. We visit more iconic landmarks in a single day than any other Las Vegas tour — Santa Monica Beach, Warner Bros Studios, Beverly Hills, Rodeo Drive, celebrity mansions, the Hollywood Walk of Fame, and the Hollywood Sign. At $215 per person all-inclusive, we offer the best value of any premium Las Vegas day trip.',
  },
];

const FAQ = () => {
  return (
    <section id="faq" className="section-padding bg-[#FDFBF7]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="font-caveat text-xl sm:text-2xl text-[#FF007A] mb-2">Got Questions?</p>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] px-5 sm:px-6 border-b-2"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-outfit font-bold text-sm sm:text-base text-left py-5 hover:no-underline [&>svg]:w-5 [&>svg]:h-5 [&>svg]:text-[#FF007A]">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="font-outfit text-sm sm:text-base text-[#4A4A4A] leading-relaxed pb-5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <p className="font-outfit text-base text-[#4A4A4A] mb-4">
            Still have questions? Our team is available 24/7.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="tel:+18777418390" className="btn-secondary px-6 py-3 text-sm" data-testid="faq-call-cta">
              CALL 1-877-741-8390
            </a>
            <a href="mailto:rsvp@vegas2hollywood.com" className="font-outfit font-bold text-sm text-[#FF007A] hover:underline" data-testid="faq-email-cta">
              rsvp@vegas2hollywood.com
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
